import {
  AppBar as MuiAppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Menu,
  MenuItem,
  Avatar,
  Badge,
  ToggleButtonGroup,
  ToggleButton,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Brightness4,
  Brightness7,
  AccountCircle,
  NotificationsNone,
  Dashboard,
  CalendarMonth,
  People,
  EventBusy,
  Assessment,
  ImportExport,
  Settings,
  Close,
} from '@mui/icons-material';
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUIStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import { useLogout } from '@/hooks/useLogout';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import { SchoolContextSwitcher } from './superadmin/SchoolContextSwitcher';

interface AppBarProps {
  drawerWidth: number;
}

export function AppBar({ drawerWidth }: AppBarProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { theme: themeMode, toggleTheme, sidebarOpen, toggleSidebar } = useUIStore();
  const user = useAuthStore((state) => state.user);
  const role = useAuthStore((state) => state.role);
  const logoutMutation = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const {
    canEditTimetable,
    canViewMasterData,
    canViewAbsences,
    canViewReports,
    canUseImportExport,
    canViewSettings,
  } = useFeatureAccess();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logoutMutation.mutate();
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };
  const pageTitleMap: Record<string, string> = {
    '/app/dashboard': 'Dashboard',
    '/app/timetable': 'Timetable Designer',
    '/app/master-data': 'Master Data',
    '/app/absences': 'Absences',
    '/app/substitutions': 'Substitutions',
    '/app/reports': 'Reports',
    '/app/import-export': 'Import / Export',
    '/app/settings': 'Settings',
  };
  const currentPageTitle = pageTitleMap[location.pathname] ?? 'Dashboard';

  const navItems = [
    { label: 'Dashboard', path: '/app/dashboard', icon: <Dashboard />, show: true },
    { label: 'Timetable', path: '/app/timetable', icon: <CalendarMonth />, show: canEditTimetable },
    { label: 'Master Data', path: '/app/master-data', icon: <People />, show: canViewMasterData },
    { label: 'Absences', path: '/app/absences', icon: <EventBusy />, show: canViewAbsences },
    { label: 'Substitutions', path: '/app/substitutions', icon: <EventBusy />, show: canViewAbsences },
    { label: 'Reports', path: '/app/reports', icon: <Assessment />, show: canViewReports },
    { label: 'Import/Export', path: '/app/import-export', icon: <ImportExport />, show: canUseImportExport },
    { label: 'Settings', path: '/app/settings', icon: <Settings />, show: canViewSettings },
  ].filter((item) => item.show);

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <>
      <MuiAppBar
        position="fixed"
        color="default"
        sx={{
          width: { sm: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { sm: sidebarOpen ? `${drawerWidth}px` : 0 },
          transition: 'width 0.3s, margin 0.3s',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={isMobile ? handleMobileMenuToggle : toggleSidebar}
            sx={{ mr: 2 }}
            aria-label={isMobile ? 'open mobile menu' : 'toggle sidebar'}
          >
            <MenuIcon />
          </IconButton>

          {!isMobile ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Typography variant="body2" color="text.secondary">
                Timetable
              </Typography>
              <Typography variant="h6" sx={{ fontSize: '1.25rem', fontWeight: 700 }}>
                {currentPageTitle}
              </Typography>
            </Box>
          ) : null}

          {/* Spacer to push School Context Switcher to the right */}
          <Box sx={{ flexGrow: 1 }} />

          {/* School Context Switcher - Super Admin Only */}
          <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
            <SchoolContextSwitcher />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!isMobile && (
              <Badge variant="dot" color="error">
                <IconButton color="inherit" aria-label="notifications">
                  <NotificationsNone />
                </IconButton>
              </Badge>
            )}

            <ToggleButtonGroup
              size="small"
              exclusive
              value={themeMode}
              onChange={(_, newValue: 'light' | 'dark' | null) => {
                if (!newValue || newValue === themeMode) return;
                toggleTheme();
              }}
              aria-label="theme mode"
              sx={{
                backgroundColor: 'background.paper',
                borderRadius: 6,
                '& .MuiToggleButtonGroup-grouped': {
                  px: 1.4,
                  py: 0.4,
                  border: 0,
                },
                '& .Mui-selected': {
                  backgroundColor: 'secondary.light !important',
                  color: 'secondary.dark',
                },
              }}
            >
              <ToggleButton value="light" aria-label="light mode">
                <Brightness7 sx={{ mr: 0.5, fontSize: 16 }} />
                <Typography variant="caption">Light</Typography>
              </ToggleButton>
              <ToggleButton value="dark" aria-label="dark mode">
                <Brightness4 sx={{ mr: 0.5, fontSize: 16 }} />
                <Typography variant="caption">Dark</Typography>
              </ToggleButton>
            </ToggleButtonGroup>

            <IconButton
              color="inherit"
              onClick={handleMenuOpen}
              aria-label="user menu"
            >
              {user?.email ? (
                <Avatar sx={{ width: 32, height: 32 }}>
                  {user.email[0].toUpperCase()}
                </Avatar>
              ) : (
                <AccountCircle />
              )}
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem disabled>
                <Box>
                  <Typography variant="body2">{user?.email}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {role}
                  </Typography>
                </Box>
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { handleMenuClose(); navigate('/app/settings'); }}>
                Settings
              </MenuItem>
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </MuiAppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={handleMobileMenuToggle}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: 280 },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Menu</Typography>
          <IconButton onClick={handleMobileMenuToggle} aria-label="close menu">
            <Close />
          </IconButton>
        </Box>
        <Divider />
        <List>
          {navItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleNavigation(item.path)}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
    </>
  );
}
